import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Building2, KeyRound, UserMinus } from 'lucide-react';
import {
  ApiError,
  cancelInvite,
  createOrgInvite,
  getOrgInvites,
  getOrgMembers,
  getOrgSso,
  qk,
  removeOrgMember,
  resendInvite,
  saveOrgSso,
  setOrgMemberRole,
  updateOrgSettings,
  type OrgMember,
  type OrgSso,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import { LoadingBlock } from '../components/Spinner.js';
import { useOrgContext } from '../components/Layout.js';
import { ORG_ROLE_LABELS, orgRoleAtLeast, type OrgRole } from '../lib/auth.js';
import { formatDate } from '../lib/format.js';

/**
 * §42.5 + §46.5 — Organizasyon sayfası: ORG üyeleri ve kurumsal giriş (SSO).
 *
 * NEDEN İKİSİ AYNI SAYFADA: ikisi de ORG uzayına aittir ve §46.7 onları birbirine
 * BAĞLAR — SSO'ya geçen bir org, halihazırda parolayla kayıtlı çalışanlarını önce
 * ORG ÜYESİ yapmak zorundadır (JIT provisioning var olan bir hesabı ancak üyeyse
 * içeri alır; hesap devralma açığının kapatılma biçimi budur). Üye ekranı olmadan
 * SSO yapılandırması operatörün elinde çözümsüz bir kilit olurdu.
 *
 * AYARLAR'DAN AYRI: `/ayarlar` PROJE üyeliklerini (§27.7 `memberships(user_id, pid)`)
 * yönetir. İki uzay §42.2'de bilinçle ayrılmıştır — org'dan çıkarmak proje erişimini
 * KALDIRMAZ ve bu sayfa bunu açıkça söyler; tek bir "Üyeler" ekranında birleştirmek,
 * operatöre yaptığından fazlasını yaptığını düşündürürdü.
 */

/** §42.5 panel yolu — Layout rayı ve App rotası buna bağlanır. */
export const ORG_PATH = '/organizasyon';

const ORG_ROLES: OrgRole[] = ['member', 'admin', 'owner'];

/** §42.5 — rolün ORG uzayında ne yaptığı; seçicinin yardım metni. */
const ORG_ROLE_DESCRIPTIONS: Record<OrgRole, string> = {
  member: 'Org listelerinde görünür; plan/fatura/SSO yönetemez.',
  admin: 'Üye ve plan/fatura yönetir, proje açar.',
  owner: 'Tam yetki: SSO yapılandırması ve org silme dahil.',
};

/** §42.5/§46.5 hata kodlarını operatörün anlayacağı tek cümleye çevirir. */
function hataMetni(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'last_owner') {
      return 'Organizasyonun son sahibi çıkarılamaz veya rolü düşürülemez — org yönetilemez hâle gelirdi.';
    }
    if (error.code === 'plan_required') {
      return 'Kurumsal giriş (SSO) bu planda yok. Faturalama sayfasından planı yükseltebilirsiniz.';
    }
    if (error.status === 403) return 'Bu işlem için yetkiniz yok.';
    return error.message;
  }
  return String(error);
}

/* -------------------------------------------------------------------------- */
/* §42.5 org üyeleri                                                           */
/* -------------------------------------------------------------------------- */

function UyelerBolumu({ oid, orgRole }: { oid: string; orgRole: OrgRole | null }) {
  const client = useQueryClient();
  /*
   * Uç `member`a AÇIKTIR (liste), yazma işleri `admin` ister. Panel kontrolü GÜVENLİK
   * DEĞİL nezakettir — sunucu kararı tekrar verir; amaç kesin 403 alacak bir seçiciyi
   * tıklatmamak.
   */
  const yetkili = orgRoleAtLeast(orgRole, 'admin');

  const uyeler = useQuery({
    queryKey: qk.orgMembers(oid),
    queryFn: ({ signal }) => getOrgMembers(oid, signal),
  });

  const rolDegistir = useMutation({
    mutationFn: (v: { userId: string; role: OrgRole }) => setOrgMemberRole(oid, v.userId, v.role),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.orgMembers(oid) });
    },
  });

  const cikar = useMutation({
    mutationFn: (userId: string) => removeOrgMember(oid, userId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.orgMembers(oid) });
    },
  });

  const yazmaHatasi = rolDegistir.error ?? cikar.error;

  return (
    <Card
      title="Organizasyon üyeleri"
      subtitle="Org rolü plan, fatura, SSO ve proje AÇMA yetkisini belirler; proje verisine erişim VERMEZ (§42.2)."
      actions={
        orgRole !== null ? (
          <Badge tone={orgRole === 'owner' ? 'accent' : 'neutral'} title={ORG_ROLE_DESCRIPTIONS[orgRole]}>
            rolünüz: {ORG_ROLE_LABELS[orgRole]}
          </Badge>
        ) : null
      }
    >
      {!yetkili ? (
        <p data-testid="org-uye-yetkisi-yok" className="mb-3 text-[12px] text-[#6b7280]">
          Üye listesi salt-okunurdur. Rol değiştirmek ve üye çıkarmak org yöneticisinin
          (admin) yetkisindedir.
        </p>
      ) : null}

      {yazmaHatasi !== null ? (
        <p data-testid="org-uye-hata" role="alert" className="mb-3 text-[12px] text-red-600">
          {hataMetni(yazmaHatasi)}
        </p>
      ) : null}

      {uyeler.isLoading ? (
        <LoadingBlock />
      ) : uyeler.isError ? (
        <ErrorState title="Üyeler okunamadı" description={hataMetni(uyeler.error)} />
      ) : (uyeler.data?.members ?? []).length === 0 ? (
        /*
         * §74.2 — boş durum artık ÇIKIŞLI. Eskiden yalnız "proje daveti kabul
         * edildiğinde üyelik kendiliğinden yazılır" diyordu: yani org yöneticisine
         * "birini org'a eklemek istiyorsan önce bir PROJEYE davet et" demek oluyordu —
         * vermek istemediği bir proje erişimini vermeye zorluyordu (§42.2'nin ayırmak
         * istediği tam da bu). Artık doğrudan davet edilebiliyor.
         */
        <p className="text-[13px] text-[#6b7280]">
          Bu organizasyonda üye görünmüyor. Aşağıdaki formla doğrudan davet edebilir
          ya da bir proje daveti gönderebilirsiniz (kabul edilince org üyeliği
          kendiliğinden yazılır, §42.5).
        </p>
      ) : (
        <div className="overflow-x-auto" data-testid="org-uyeler-listesi">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] text-[#9ca3af]">
                <th className="py-1 pr-4">Ad soyad</th>
                <th className="py-1 pr-4">E-posta</th>
                <th className="py-1 pr-4">Org rolü</th>
                <th className="py-1 pr-4">Katıldı</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {(uyeler.data?.members ?? []).map((uye: OrgMember) => (
                <tr key={uye.user_id} className="border-t border-[#f3f4f6]" data-testid="org-uye-satiri">
                  <td className="py-1.5 pr-4 text-[#111827]">{uye.full_name ?? '—'}</td>
                  <td className="py-1.5 pr-4 text-[#6b7280]">{uye.email}</td>
                  <td className="py-1.5 pr-4">
                    {yetkili ? (
                      <select
                        data-testid="org-uye-rol"
                        value={uye.role}
                        disabled={rolDegistir.isPending}
                        onChange={(e) =>
                          rolDegistir.mutate({
                            userId: uye.user_id,
                            role: e.target.value as OrgRole,
                          })
                        }
                        className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-2 text-[13px] outline-none"
                      >
                        {ORG_ROLES.map((r) => (
                          <option key={r} value={r} title={ORG_ROLE_DESCRIPTIONS[r]}>
                            {ORG_ROLE_LABELS[r]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Badge tone="neutral" size="sm" title={ORG_ROLE_DESCRIPTIONS[uye.role]}>
                        {ORG_ROLE_LABELS[uye.role]}
                      </Badge>
                    )}
                  </td>
                  <td className="py-1.5 pr-4 text-[#6b7280]">{formatDate(uye.created_at)}</td>
                  <td className="py-1.5 text-right">
                    {yetkili ? (
                      <button
                        type="button"
                        data-testid="org-uye-cikar"
                        disabled={cikar.isPending}
                        onClick={() => cikar.mutate(uye.user_id)}
                        /* §42.2 — çıkarmanın NE YAPMADIĞI da söylenir; sessiz bir
                           "kaldır", operatöre proje erişimini de kestiğini sandırırdı. */
                        title="Org üyeliğini kaldırır. PROJE üyelikleri dokunulmadan kalır (§42.2)."
                        className="inline-flex items-center gap-1 rounded-lg border border-[#e5e7eb] px-2 py-1 text-[12px] font-medium text-red-700 disabled:opacity-50"
                      >
                        <UserMinus size={12} /> Çıkar
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* §46.5 SSO yapılandırması                                                    */
/* -------------------------------------------------------------------------- */

function SsoBolumu({ oid, orgRole }: { oid: string; orgRole: OrgRole | null }) {
  const client = useQueryClient();
  /* §46.5 — yapılandırma ucu org:owner ister (RBAC tablosu); admin bile yazamaz. */
  const yetkili = orgRoleAtLeast(orgRole, 'owner');

  const sso = useQuery({
    queryKey: qk.orgSso(oid),
    queryFn: ({ signal }) => getOrgSso(oid, signal),
    retry: false,
  });

  const [issuer, setIssuer] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [enforced, setEnforced] = useState(false);

  /*
   * Form sunucudan gelen değerle DOLDURULUR ama `clientSecret` BOŞ kalır: sır hiç
   * dönmez (§46.5 `clientSecretSet`) ve boş gönderim "mevcut olanı koru" demektir.
   * Yıldızlarla dolu sahte bir kutu çizmek, kullanıcıya sırrı okuyabildiğimizi
   * düşündürürdü.
   */
  const mevcut: OrgSso | null = sso.data?.sso ?? null;
  useEffect(() => {
    if (mevcut === null) return;
    setIssuer(mevcut.issuer);
    setClientId(mevcut.clientId);
    setEmailDomain(mevcut.emailDomain ?? '');
    setEnforced(mevcut.enforced);
  }, [mevcut]);

  const kaydet = useMutation({
    mutationFn: () =>
      saveOrgSso(oid, {
        issuer: issuer.trim(),
        clientId: clientId.trim(),
        ...(clientSecret.trim() === '' ? {} : { clientSecret: clientSecret.trim() }),
        emailDomain: emailDomain.trim(),
        enforced,
      }),
    onSuccess: () => {
      setClientSecret('');
      void client.invalidateQueries({ queryKey: qk.orgSso(oid) });
    },
  });

  if (sso.isLoading) return <LoadingBlock />;
  if (sso.isError) {
    return <ErrorState title="SSO yapılandırması okunamadı" description={hataMetni(sso.error)} />;
  }

  const planAllows = sso.data?.planAllows === true;
  /*
   * §46.1 — plan izin vermiyorsa YENİ yapılandırma engellenir ama MEVCUT yapılandırma
   * ÇALIŞMAYA DEVAM EDER: düşürülmüş bir org'un çalışanlarını kapı dışında bırakmak
   * veri kaybından beterdir. Form bu yüzden "kilitli" olur, yapılandırma SİLİNMEZ.
   */
  const kilitli = !planAllows || !yetkili;

  return (
    <Card
      title="Kurumsal giriş (SSO)"
      subtitle="OIDC Authorization Code akışı. SAML desteklenmez (§46.1) — gerçek bir müşteri talebine ertelenmiştir."
      actions={
        <div className="flex items-center gap-2">
          <Badge tone={planAllows ? 'success' : 'outline'} size="sm">
            {planAllows ? 'plan destekliyor' : 'plan desteklemiyor'}
          </Badge>
          {mevcut !== null ? (
            <Badge tone={mevcut.enforced ? 'accent' : 'neutral'} size="sm">
              {mevcut.enforced ? 'zorunlu' : 'isteğe bağlı'}
            </Badge>
          ) : null}
        </div>
      }
    >
      {!yetkili ? (
        <p data-testid="sso-yetkisi-yok" className="text-[13px] text-[#6b7280]">
          Kurumsal giriş yapılandırmasını yalnız organizasyon sahibi (owner) değiştirebilir.
        </p>
      ) : null}

      {!planAllows && mevcut !== null ? (
        <p data-testid="sso-plan-dusuk" className="mb-3 rounded-md bg-[#fffbeb] px-3 py-2 text-[13px] text-amber-700">
          Planınız SSO’yu içermiyor, ancak <strong>mevcut yapılandırmayla giriş sürüyor</strong>{' '}
          (§46.1). Yalnız değişiklik yapmak engellidir.
        </p>
      ) : null}

      <form
        data-testid="sso-formu"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!kilitli && issuer.trim() !== '' && clientId.trim() !== '') kaydet.mutate();
        }}
      >
        <div>
          <label className="text-[12px] text-[#6b7280]" htmlFor="sso-issuer">
            Issuer (discovery kökü)
          </label>
          <input
            id="sso-issuer"
            data-testid="sso-issuer"
            value={issuer}
            disabled={kilitli}
            onChange={(e) => setIssuer(e.target.value)}
            placeholder="https://idp.sirket.com"
            className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 font-mono text-[13px] disabled:bg-[#f9fafb]"
          />
          <p className="mt-1 text-[12px] text-[#9ca3af]">
            <code>/.well-known/openid-configuration</code> bu adresten okunur; anahtarlar
            (JWKS) 1 saat önbelleklenir.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[12px] text-[#6b7280]" htmlFor="sso-client-id">
              Client ID
            </label>
            <input
              id="sso-client-id"
              data-testid="sso-client-id"
              value={clientId}
              disabled={kilitli}
              onChange={(e) => setClientId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 font-mono text-[13px] disabled:bg-[#f9fafb]"
            />
          </div>
          <div>
            <label className="text-[12px] text-[#6b7280]" htmlFor="sso-client-secret">
              Client secret
            </label>
            <input
              id="sso-client-secret"
              data-testid="sso-client-secret"
              type="password"
              value={clientSecret}
              disabled={kilitli}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={mevcut?.clientSecretSet === true ? 'değiştirmek için yazın' : ''}
              className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 font-mono text-[13px] disabled:bg-[#f9fafb]"
            />
            {/* §46.5 — sır GERİ OKUNMAZ; "yazılı mı" bilgisi yeterlidir. */}
            <p className="mt-1 text-[12px] text-[#9ca3af]">
              {mevcut?.clientSecretSet === true
                ? 'Kayıtlı sır gösterilmez. Boş bırakırsanız değişmez.'
                : 'İlk yapılandırmada zorunludur.'}
            </p>
          </div>
        </div>

        <div>
          <label className="text-[12px] text-[#6b7280]" htmlFor="sso-domain">
            E-posta alan adı (opsiyonel)
          </label>
          <input
            id="sso-domain"
            data-testid="sso-domain"
            value={emailDomain}
            disabled={kilitli}
            onChange={(e) => setEmailDomain(e.target.value)}
            placeholder="sirket.com"
            className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 font-mono text-[13px] disabled:bg-[#f9fafb]"
          />
          <p className="mt-1 text-[12px] text-[#9ca3af]">
            Giriş sayfasındaki “Kurumsal giriş” bağlantısı org’u bu alan adından bulur
            (§46.3). Yazılıysa IdP’nin iddia ettiği adres de bu alanda olmak zorundadır.
          </p>
        </div>

        <label className="flex items-start gap-2 text-[13px] text-[#374151]">
          <input
            type="checkbox"
            data-testid="sso-enforced"
            checked={enforced}
            disabled={kilitli}
            onChange={(e) => setEnforced(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <strong>Parola girişini kapat (zorunlu SSO).</strong>{' '}
            {/* §46.4 — break-glass olmadan bu kutu bir kilitlenme düğmesi olurdu. */}
            <span className="text-[#6b7280]">
              Organizasyon sahibi, IdP bozulursa diye parolayla girmeye devam edebilir
              (break-glass, §46.4).
            </span>
          </span>
        </label>

        {kaydet.isError ? (
          <p data-testid="sso-hata" role="alert" className="text-[12px] text-red-600">
            {hataMetni(kaydet.error)}
          </p>
        ) : kaydet.isSuccess ? (
          <p data-testid="sso-kaydedildi" className="text-[12px] text-[#6b7280]">
            Yapılandırma kaydedildi.
          </p>
        ) : null}

        <button
          type="submit"
          data-testid="sso-kaydet"
          disabled={kilitli || kaydet.isPending || issuer.trim() === '' || clientId.trim() === ''}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white disabled:opacity-50"
        >
          <KeyRound size={14} /> {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </form>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * §72.5/6 — org ADI düzenleme.
 *
 * `POST /api/orgs/:oid/settings` M23'ten beri sunucuda VARDI ama `api.ts` içinde
 * istemcisi bile yoktu: org'un adını değiştirmenin panelde hiçbir yolu olmadan,
 * kurulum sihirbazında yazılan ilk ad kalıcıydı. Tamamen ölü bir sunucu yüzeyiydi.
 *
 * `slug` DEĞİŞTİRİLEMEZ ve bu ekranda SÖYLENİR: SSO adresi ve paylaşılmış bağlantılar
 * ona bağlıdır (§42.3).
 */
/**
 * §74.2 — org'a DOĞRUDAN davet + bekleyen davetler.
 *
 * Proje davetiyle AYNI tabloyu ve token makinesini kullanır (§74.2 kararı); farkı
 * kabulde YALNIZ org üyeliği yazmasıdır — proje verisine erişim VERMEZ (§42.2).
 */
function OrgDavetBolumu({ oid, orgRole }: { oid: string; orgRole: OrgRole | null }) {
  const client = useQueryClient();
  const yetkili = orgRoleAtLeast(orgRole, 'admin');
  const [email, setEmail] = useState('');
  const [rol, setRol] = useState<OrgRole>('member');

  const davetler = useQuery({
    queryKey: qk.orgInvites(oid),
    queryFn: ({ signal }) => getOrgInvites(oid, signal),
    retry: false,
    // Uç org:admin ister; yetkisiz kullanıcı için istek bile atılmaz (403 gürültüsü olmasın).
    enabled: yetkili,
  });

  const davet = useMutation({
    mutationFn: () => createOrgInvite(oid, { email: email.trim().toLowerCase(), role: rol }),
    onSuccess: () => {
      // Adres temizlenir, ROL KALIR — arka arkaya aynı rolde davet etmek yaygın.
      setEmail('');
      void client.invalidateQueries({ queryKey: qk.orgInvites(oid) });
    },
  });

  const iptal = useMutation({
    mutationFn: (id: number) => cancelInvite(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.orgInvites(oid) }),
  });

  const yeniden = useMutation({
    mutationFn: (id: number) => resendInvite(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.orgInvites(oid) }),
  });

  if (!yetkili) return null;

  const hata = davet.error ?? iptal.error ?? yeniden.error;
  const bekleyen = davetler.data?.invites ?? [];

  return (
    <Card
      title="Organizasyona davet"
      subtitle="Davet edilen kişi kabul ettiğinde YALNIZ org üyeliği kazanır; hiçbir projenin verisine erişemez (§42.2)."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        data-testid="org-davet-formu"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim() !== '' && !davet.isPending) davet.mutate();
        }}
      >
        <label className="flex min-w-[240px] flex-1 flex-col gap-1">
          <span className="text-[12px] text-[#6b7280]">E-posta adresi</span>
          <input
            data-testid="org-davet-eposta"
            type="email"
            value={email}
            maxLength={254}
            placeholder="ad.soyad@sirket.com"
            onChange={(event) => setEmail(event.target.value)}
            className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-[#6b7280]">Org rolü</span>
          <select
            data-testid="org-davet-rol"
            value={rol}
            onChange={(event) => setRol(event.target.value as OrgRole)}
            className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-2 text-[13px] text-[#111827] outline-none focus:border-[#d1d5db]"
          >
            {ORG_ROLES.map((r) => (
              <option key={r} value={r} title={ORG_ROLE_DESCRIPTIONS[r]}>
                {ORG_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          data-testid="org-davet-gonder"
          className="btn-primary"
          disabled={email.trim() === '' || davet.isPending}
        >
          {davet.isPending ? 'Gönderiliyor…' : 'Davet gönder'}
        </button>
      </form>

      {hata !== null && hata !== undefined ? (
        <p data-testid="org-davet-hata" role="alert" className="mt-2 text-[12px] text-red-600">
          {hataMetni(hata)}
        </p>
      ) : null}

      {bekleyen.length > 0 ? (
        <div className="mt-4" data-testid="org-bekleyen-davetler">
          <p className="mb-2 text-[12px] font-semibold text-[#6b7280]">Bekleyen davetler</p>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] text-[#9ca3af]">
                <th className="py-1 pr-4">E-posta</th>
                <th className="py-1 pr-4">Rol</th>
                <th className="py-1 pr-4">Geçerlilik</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {bekleyen.map((d) => (
                <tr key={d.id} className="border-t border-[#f3f4f6]">
                  <td className="py-1.5 pr-4 font-mono text-[12px] text-[#374151]">{d.email}</td>
                  <td className="py-1.5 pr-4">
                    <Badge tone="neutral" size="sm">
                      {ORG_ROLE_LABELS[d.role]}
                    </Badge>
                  </td>
                  <td className="py-1.5 pr-4 text-[12px] text-[#9ca3af]">
                    {formatDate(d.expiresAt)}
                  </td>
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      data-testid="org-davet-yeniden"
                      className="btn-ghost"
                      disabled={yeniden.isPending}
                      onClick={() => yeniden.mutate(d.id)}
                      title="Yeni bir bağlantı üretir; eskisi geçersiz olur"
                    >
                      Yeniden gönder
                    </button>
                    <button
                      type="button"
                      data-testid="org-davet-iptal"
                      className="btn-ghost"
                      disabled={iptal.isPending}
                      onClick={() => iptal.mutate(d.id)}
                    >
                      İptal
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}

function OrgAdiBolumu({ oid, orgRole }: { oid: string; orgRole: OrgRole | null }) {
  const queryClient = useQueryClient();
  const { orgs } = useOrgContext();
  const org = orgs.find((o) => o.id === oid);
  const [ad, setAd] = useState('');

  const kaydet = useMutation({
    mutationFn: () => updateOrgSettings(oid, { name: ad.trim() }),
    onSuccess: async () => {
      setAd('');
      // Seçicideki ve başlıktaki ad da tazelensin — ikisi de `qk.orgs()`ten okur.
      await queryClient.invalidateQueries({ queryKey: qk.orgs() });
    },
  });

  // Uç org:admin ister; yetkisiz kullanıcıya kesin 403 alacak bir form çizilmez.
  if (!orgRoleAtLeast(orgRole, 'admin')) return null;

  return (
    <Card
      title="Organizasyon adı"
      subtitle="Kısa ad (slug) DEĞİŞTİRİLEMEZ: kurumsal giriş adresi ve paylaşılmış bağlantılar ona bağlıdır."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        data-testid="org-ad-formu"
        onSubmit={(event) => {
          event.preventDefault();
          if (ad.trim() !== '' && !kaydet.isPending) kaydet.mutate();
        }}
      >
        <label className="flex min-w-[240px] flex-1 flex-col gap-1">
          <span className="text-[12px] text-[#6b7280]">Ad</span>
          <input
            data-testid="org-ad"
            type="text"
            value={ad}
            placeholder={org?.name ?? ''}
            maxLength={120}
            onChange={(event) => setAd(event.target.value)}
            className="h-8 rounded-lg border border-[#e5e7eb] bg-white px-3 text-[13px] text-[#111827] outline-none placeholder:text-[#9ca3af] focus:border-[#d1d5db]"
          />
        </label>
        <span className="flex flex-col gap-1">
          <span className="text-[12px] text-[#6b7280]">Kısa ad</span>
          <span className="flex h-8 items-center rounded-lg bg-[#f3f4f6] px-3 font-mono text-[12px] text-[#6b7280]">
            {org?.slug ?? '—'}
          </span>
        </span>
        <button
          type="submit"
          data-testid="org-ad-kaydet"
          className="btn-primary"
          disabled={ad.trim() === '' || kaydet.isPending}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </form>
      {kaydet.error !== null ? (
        <p role="alert" className="mt-2 text-[12px] text-red-600">
          {(kaydet.error as Error).message}
        </p>
      ) : null}
    </Card>
  );
}

export default function Organizasyon() {
  const { orgId, orgs, orgRole } = useOrgContext();
  const orgAdi = orgs.find((o) => o.id === orgId)?.name ?? null;

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="organizasyon-sayfasi">
      <div className="border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <Building2 size={18} className="text-[#6b7280]" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[18px] font-semibold text-[#111827]">
                {orgAdi ?? 'Organizasyon'}
              </h1>
              <BilgiDugmesi testId="organizasyon-bilgi">Organizasyon üyelerini, rollerini ve davetleri buradan yönetirsiniz; kurumsal giriş (SSO/OIDC) yapılandırması da buradadır (§46). Projeye özgü ayarlar için "Ayarlar" sayfasına bakın.</BilgiDugmesi>
            </div>
            <p className="text-[13px] text-[#6b7280]">
              Org üyeleri ve kurumsal giriş — proje ayarları için “Ayarlar” sayfasına bakın.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6 px-6 py-8">
        {orgId === null ? (
          <EmptyState
            title="Organizasyon seçilmedi"
            description="Sol üstten bir organizasyon seçin; üyeler ve kurumsal giriş org’a asılıdır."
          />
        ) : (
          <>
            {/* §72.5/6 — org adı EN ÜSTTE: sayfanın konusu bu org'dur. */}
            <OrgAdiBolumu oid={orgId} orgRole={orgRole} />
            <UyelerBolumu oid={orgId} orgRole={orgRole} />
            {/* §74.2 — davet, üye listesinin HEMEN ARDINDA: listedeki boşluğun çözümü. */}
            <OrgDavetBolumu oid={orgId} orgRole={orgRole} />
            <SsoBolumu oid={orgId} orgRole={orgRole} />
          </>
        )}
      </div>
    </div>
  );
}
